import "@saleor/macaw-ui-next/style";
import "./index.css";

import maxceiFavicon from "@assets/images/maxcei-emblem.png";

// Set the MAXCEI favicon (the static index.html has no <link rel="icon">).
const faviconLink = document.createElement("link");

faviconLink.rel = "icon";
faviconLink.type = "image/png";
faviconLink.href = maxceiFavicon;
document.head.appendChild(faviconLink);

import { ApolloProvider } from "@apollo/client";
import { history, Route, Router } from "@dashboard/components/Router";
import { ExtensionsPaths, extensionsSection } from "@dashboard/extensions/urls";
import { PermissionEnum } from "@dashboard/graphql";
import useAppState from "@dashboard/hooks/useAppState";
import { pageListPath } from "@dashboard/modeling/urls";
import { modelTypesPath } from "@dashboard/modelTypes/urls";
import { RefundsSettingsRoute } from "@dashboard/refundsSettings/route";
import { refundsSettingsPath } from "@dashboard/refundsSettings/urls";
import { structuresListPath } from "@dashboard/structures/urls";
import { ThemeProvider } from "@dashboard/theme";
import { OnboardingProvider } from "@dashboard/welcomePage/WelcomePageOnboarding/onboardingContext";
import { ThemeProvider as LegacyThemeProvider } from "@saleor/macaw-ui";
import { SaleorProvider } from "@saleor/sdk";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import TagManager from "react-gtm-module";
import { useIntl } from "react-intl";
import { Redirect, Switch } from "react-router-dom";

import AccessLogSection from "./accessLog";
import AttributeSection from "./attributes";
import { attributeSection } from "./attributes/urls";
import Auth from "./auth";
import AuthProvider from "./auth/AuthProvider";
import LoginLoading from "./auth/components/LoginLoading/LoginLoading";
import SectionRoute from "./auth/components/SectionRoute";
import { useAuthRedirection } from "./auth/hooks/useAuthRedirection";
import CartVisibilitySection from "./cartVisibility";
import CategorySection from "./categories";
import ChannelsSection from "./channels";
import { channelsSection } from "./channels/urls";
import CollectionSection from "./collections";
import AppLayout from "./components/AppLayout";
import useAppChannel, { AppChannelProvider } from "./components/AppLayout/AppChannelContext";
import { DateProvider } from "./components/Date";
import { DevModeProvider } from "./components/DevModePanel/DevModeProvider";
import ErrorPage from "./components/ErrorPage";
import ExitFormDialogProvider from "./components/Form/ExitFormDialogProvider";
import { legacyRedirects } from "./components/LegacyRedirects";
import { LocaleProvider } from "./components/Locale";
import MessageManagerProvider from "./components/messages";
import { NavigatorSearchProvider } from "./components/NavigatorSearch/NavigatorSearchProvider";
import { ProductAnalytics } from "./components/ProductAnalytics";
import { SavebarRefProvider } from "./components/Savebar/SavebarRefContext";
import { ShopProvider } from "./components/Shop";
import { TimezoneProvider } from "./components/Timezone";
import { WindowTitle } from "./components/WindowTitle";
import { GTM_ID } from "./config";
import ConfigurationSection from "./configuration";
import { getConfigMenuItemsPermissions } from "./configuration/utils";
import AppStateProvider from "./containers/AppState";
import BackgroundTasksProvider from "./containers/BackgroundTasks";
import { CustomerSection } from "./customers";
import DiscountSection from "./discounts";
import ExportSection from "./exports";
import { exportSection } from "./exports/urls";
import { ExtensionsSection } from "./extensions";
import { ExternalAppProvider } from "./extensions/components/ExternalAppContext/ExternalAppContext";
import { FeatureFlagsProviderWithUser } from "./featureFlags/FeatureFlagsProvider";
import FeedbackSection from "./feedback";
import GiftCardSection from "./giftCards";
import { giftCardsSectionUrlName } from "./giftCards/urls";
import { apolloClient, saleorClient } from "./graphql/client";
import { useLocationState } from "./hooks/useLocationState";
import ImportSection from "./imports";
import { importSection } from "./imports/urls";
import { commonMessages } from "./intl";
import LabelPrintingSection from "./labels";
import { labelPrintingSection } from "./labels/urls";
import PageSection from "./modeling";
import PageTypesSection from "./modelTypes";
import NewsletterSection from "./newsletter";
import { NotFound } from "./NotFound";
import OpeningHoursSection from "./openingHours";
import { isOperatorHost } from "./operator/operatorHost";
import { OperatorRoutes } from "./operator/OperatorRoutes";
import OrdersSection from "./orders";
import PermissionGroupSection from "./permissionGroups";
import { isPortalHost } from "./portal/portalHost";
import { PortalRoutes } from "./portal/PortalRoutes";
import ProductAlertsSection from "./productAlerts";
import ProductSection from "./products";
import ProductTypesSection from "./productTypes";
import SearchSection from "./search";
import errorTracker from "./services/errorTracking";
import ShippingSection from "./shipping";
import SiteSettingsSection from "./siteSettings";
import StaffSection from "./staff";
import NavigationSection from "./structures";
import TaxesSection from "./taxes";
import { paletteOverrides, themeOverrides } from "./themeOverrides";
import { applyTimeZone, getTimeZone } from "./timeZone/timeZone";
import TranslationsSection from "./translations";
import WarehouseSection from "./warehouses";
import { warehouseSection } from "./warehouses/urls";
import { WelcomePage } from "./welcomePage";

if (GTM_ID) {
  TagManager.initialize({ gtmId: GTM_ID });
}

errorTracker.init(history);

/*
  Handle legacy theming toggle. Since we use new and old macaw,
  we need to handle both theme swticher for a while.
*/
const handleLegacyTheming = () => {
  const activeTheme = localStorage.getItem("activeMacawUITheme");

  if (activeTheme === "defaultDark") {
    localStorage.setItem("macaw-ui-theme", "dark");

    return;
  }

  localStorage.setItem("macaw-ui-theme", "light");
};

handleLegacyTheming();

// One time zone for every date shown (default Lisbon; see src/timeZone).
const timeZone = getTimeZone();

applyTimeZone(timeZone);

const App = () => (
  // @ts-expect-error legacy types
  <SaleorProvider client={saleorClient}>
    <ApolloProvider client={apolloClient}>
      <Router>
        {/* @ts-expect-error legacy types */}
        <LegacyThemeProvider overrides={themeOverrides} palettes={paletteOverrides}>
          <ThemeProvider>
            <DateProvider>
              <TimezoneProvider value={timeZone}>
                <LocaleProvider>
                  <MessageManagerProvider>
                    <BackgroundTasksProvider>
                      <AppStateProvider>
                        <AuthProvider>
                          <ProductAnalytics>
                            <ShopProvider>
                              <AppChannelProvider>
                                <ExitFormDialogProvider>
                                  <DevModeProvider>
                                    <NavigatorSearchProvider>
                                      <SavebarRefProvider>
                                        <FeatureFlagsProviderWithUser>
                                          <OnboardingProvider>
                                            {isPortalHost() ? (
                                              <PortalRoutes />
                                            ) : isOperatorHost() ? (
                                              <OperatorRoutes />
                                            ) : (
                                              <Routes />
                                            )}
                                          </OnboardingProvider>
                                        </FeatureFlagsProviderWithUser>
                                      </SavebarRefProvider>
                                    </NavigatorSearchProvider>
                                  </DevModeProvider>
                                </ExitFormDialogProvider>
                              </AppChannelProvider>
                            </ShopProvider>
                          </ProductAnalytics>
                        </AuthProvider>
                      </AppStateProvider>
                    </BackgroundTasksProvider>
                  </MessageManagerProvider>
                </LocaleProvider>
              </TimezoneProvider>
            </DateProvider>
          </ThemeProvider>
        </LegacyThemeProvider>
      </Router>
    </ApolloProvider>
  </SaleorProvider>
);
const Routes = () => {
  const intl = useIntl();
  const [, dispatchAppState] = useAppState();
  const { authenticated, authenticating } = useAuthRedirection();
  const { channel } = useAppChannel(false);
  const channelLoaded = typeof channel !== "undefined";
  const homePageLoaded = channelLoaded && authenticated;
  const homePageLoading = (authenticated && !channelLoaded) || authenticating;
  const { isAppPath } = useLocationState();

  return (
    <>
      <WindowTitle title={intl.formatMessage(commonMessages.dashboard)} />
      {homePageLoaded ? (
        <ExternalAppProvider>
          <AppLayout fullSize={isAppPath}>
            <ErrorBoundary
              onError={e => {
                const errorId = errorTracker.captureException(e);

                dispatchAppState({
                  payload: {
                    error: "unhandled",
                    errorId,
                  },
                  type: "displayError",
                });
              }}
              fallbackRender={({ resetErrorBoundary }) => (
                <ErrorPage onBack={resetErrorBoundary} onRefresh={() => window.location.reload()} />
              )}
            >
              <Switch>
                {legacyRedirects}
                <SectionRoute exact path="/" component={WelcomePage} />
                <SectionRoute
                  permissions={[
                    PermissionEnum.MANAGE_PRODUCTS,
                    PermissionEnum.MANAGE_ORDERS,
                    PermissionEnum.MANAGE_PAGES,
                    PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
                  ]}
                  matchPermission="any"
                  path="/search"
                  component={SearchSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                  path="/categories"
                  component={CategorySection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                  path="/collections"
                  component={CollectionSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_USERS]}
                  path="/customers"
                  component={CustomerSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_GIFT_CARD]}
                  path={giftCardsSectionUrlName}
                  component={GiftCardSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_DISCOUNTS]}
                  path="/discounts"
                  component={DiscountSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PAGES]}
                  path={pageListPath}
                  component={PageSection}
                />
                <SectionRoute
                  permissions={[
                    PermissionEnum.MANAGE_PAGES,
                    PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
                  ]}
                  path={modelTypesPath}
                  component={PageTypesSection}
                  matchPermission="any"
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_ORDERS]}
                  path="/orders"
                  component={OrdersSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_ORDERS]}
                  path="/live-carts"
                  component={CartVisibilitySection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_USERS]}
                  path="/newsletter"
                  component={NewsletterSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_USERS]}
                  path="/feedback"
                  component={FeedbackSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_USERS]}
                  path="/product-alerts"
                  component={ProductAlertsSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                  path="/products"
                  component={ProductSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                  path={importSection}
                  component={ImportSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                  path={exportSection}
                  component={ExportSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                  path={labelPrintingSection}
                  component={LabelPrintingSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES]}
                  path="/product-types"
                  component={ProductTypesSection}
                />
                <SectionRoute path="/staff" component={StaffSection} />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_SETTINGS]}
                  path="/opening-hours"
                  component={OpeningHoursSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_STAFF]}
                  path="/access-log"
                  component={AccessLogSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_STAFF]}
                  path="/permission-groups"
                  component={PermissionGroupSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_SETTINGS]}
                  path="/site-settings"
                  component={SiteSettingsSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_SETTINGS]}
                  path={refundsSettingsPath}
                  component={RefundsSettingsRoute}
                />
                <SectionRoute path="/taxes" component={TaxesSection} />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_SHIPPING]}
                  path="/shipping"
                  component={ShippingSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_TRANSLATIONS]}
                  path="/translations"
                  component={TranslationsSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_MENUS]}
                  path={structuresListPath}
                  component={NavigationSection}
                />
                <SectionRoute
                  permissions={[
                    PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
                    PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
                  ]}
                  path={attributeSection}
                  component={AttributeSection}
                  matchPermission="any"
                />
                <SectionRoute
                  permissions={[]}
                  path={extensionsSection}
                  component={ExtensionsSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                  path={warehouseSection}
                  component={WarehouseSection}
                />
                <SectionRoute
                  permissions={[PermissionEnum.MANAGE_CHANNELS]}
                  path={channelsSection}
                  component={ChannelsSection}
                />
                <SectionRoute
                  matchPermission="any"
                  permissions={getConfigMenuItemsPermissions(intl)}
                  exact
                  path="/configuration"
                  component={ConfigurationSection}
                />
                <Redirect to={ExtensionsPaths.installedExtensions} path={"/apps"} />
                <Redirect to={ExtensionsPaths.installedExtensions} path="/custom-apps/" />
                <Redirect to={ExtensionsPaths.installedExtensions} path="/plugins" />
                <Route component={NotFound} />
              </Switch>
            </ErrorBoundary>
          </AppLayout>
        </ExternalAppProvider>
      ) : homePageLoading ? (
        <LoginLoading />
      ) : (
        <Auth />
      )}
    </>
  );
};

const root = createRoot(document.querySelector("#dashboard-app")!);

root.render(<App />);
